/** @jsxImportSource ../i18n */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Calculator, CalendarCheck, Hourglass, PiggyBank, Save, TrendingUp } from 'lucide-react';
import api from '../services/api';
import { ceilingUsedPct, simulateWithdrawal } from '../services/pea';
import { useToast } from '../contexts/ToastContext';
import { cx } from '../theme/cx';
import { formatDate, formatNumber, formatPercent } from '../theme/format';
import {
  Badge, Banner, Button, Card, CardHeader, Field, Input, Money, PageHeader, Percent, Progress, Skeleton,
} from '../components/ui';

const errorText = (err, fallback) => err?.response?.data?.error || fallback;

const PRESETS = [
  { label: '10 %', share: 0.1 },
  { label: '25 %', share: 0.25 },
  { label: '50 %', share: 0.5 },
  { label: 'Tout', share: 1 },
];

const Row = ({ label, hint, children, strong }) => (
  <div className="flex items-start justify-between gap-4 py-3 first:pt-0 last:pb-0">
    <dt className="text-sm text-fg-muted">
      {label}
      {hint && <span className="mt-0.5 block text-xs text-fg-subtle">{hint}</span>}
    </dt>
    <dd className={cx('num text-right', strong ? 'text-lg font-semibold text-fg' : 'text-[15px] font-medium text-fg')}>{children}</dd>
  </div>
);

const pctLabel = (rate) => formatPercent(rate, { sign: false });

const CeilingCard = ({ status }) => {
  const used = ceilingUsedPct(status);
  return (
    <Card>
      <CardHeader icon={PiggyBank} title="Plafond des versements" description="Plafond légal des versements sur la vie du plan." />
      {status.deposits === null || status.deposits === undefined ? (
        <p className="text-sm text-fg-muted">Indiquez vos versements cumulés dans le profil.</p>
      ) : (
        <>
          <p className="font-display text-display-md text-fg"><Money value={status.deposits} decimals={0} /></p>
          <p className="mt-1 text-sm text-fg-muted">versés sur <Money value={status.ceiling} decimals={0} /></p>
          <Progress value={used} max={100} label="Part du plafond utilisée" className="mt-5 h-2" />
          <div className="mt-3 flex items-center justify-between text-sm">
            <span className="num text-fg-muted">{formatPercent(used, { sign: false })} utilisés</span>
            <span className="text-fg">Reste <Money value={status.remaining} decimals={0} className="font-semibold" /></span>
          </div>
        </>
      )}
    </Card>
  );
};

const MaturityCard = ({ status }) => {
  if (!status.maturity_date) {
    return (
      <Card>
        <CardHeader icon={Hourglass} title="Antériorité fiscale" description="Après 5 ans, les gains ne supportent plus que les prélèvements sociaux." />
        <p className="text-sm text-fg-muted">Indiquez la date d’ouverture dans le profil.</p>
      </Card>
    );
  }
  const elapsed = Math.min(100, Math.max(0, ((1826 - status.days_to_maturity) / 1826) * 100));
  return (
    <Card tone={status.matured ? 'gold' : 'default'}>
      <CardHeader icon={status.matured ? CalendarCheck : Hourglass} title="Antériorité fiscale" description={`Ouvert le ${formatDate(status.opened_on, 'long')}`} />
      {status.matured ? (
        <>
          <Badge tone="gain" dot>Franchise fiscale acquise</Badge>
          <p className="mt-3 text-sm leading-relaxed text-fg-muted">
            Depuis le {formatDate(status.maturity_date, 'long')} : retraits partiels possibles sans clôture, gains soumis aux seuls prélèvements sociaux ({pctLabel(status.social_rate_pct)}).
          </p>
        </>
      ) : (
        <>
          <p className="font-display text-display-md text-fg"><span className="num">{formatNumber(status.days_to_maturity)}</span> jours</p>
          <p className="mt-1 text-sm text-fg-muted">avant les 5 ans, le {formatDate(status.maturity_date, 'long')}</p>
          <Progress value={elapsed} max={100} label="Avancement vers les 5 ans" className="mt-5 h-2" />
          <p className="mt-3 text-xs leading-relaxed text-fg-subtle">Avant cette date, tout retrait clôture le plan (hors cas prévus par la loi).</p>
        </>
      )}
    </Card>
  );
};

const GainsCard = ({ status }) => (
  <Card>
    <CardHeader icon={TrendingUp} title="Plus-value du plan" description="Valeur actuelle moins versements cumulés." />
    {status.gains === null || status.gains === undefined ? (
      <p className="text-sm text-fg-muted">{status.value_unavailable ? 'Valorisation indisponible : connectez votre courtier.' : 'Indiquez vos versements cumulés dans le profil.'}</p>
    ) : (
      <>
        <p className="font-display text-display-md"><Money value={status.gains} decimals={0} sign colorize /></p>
        <p className="mt-1 text-sm text-fg-muted">
          <Percent value={status.deposits > 0 ? (status.gains / status.deposits) * 100 : null} colorize /> sur les versements
        </p>
        <p className="mt-5 text-sm text-fg-muted">Valeur actuelle <Money value={status.current_value} decimals={0} className="font-semibold text-fg" /> <span className="text-fg-subtle">(liquidités comprises)</span></p>
      </>
    )}
  </Card>
);

const Simulator = ({ status }) => {
  const [raw, setRaw] = useState('');
  const amount = Number(String(raw).replace(/\s/g, '').replace(',', '.'));
  const result = useMemo(() => simulateWithdrawal(status, amount), [status, amount]);
  const ready = status.current_value !== null && status.current_value !== undefined && status.deposits !== null && status.deposits !== undefined;

  return (
    <Card className="lg:col-span-3">
      <CardHeader icon={Calculator} title="Simuler un retrait" description="Ce qui se passe si vous retirez un montant aujourd’hui." />
      {!ready ? (
        <Banner tone="neutral">Le simulateur a besoin de la valeur du compte et de vos versements cumulés.</Banner>
      ) : (
        <div className="space-y-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <Field label="Montant du retrait" htmlFor="pea-withdrawal" className="flex-1">
              <Input id="pea-withdrawal" inputMode="decimal" placeholder="ex. 10 000" value={raw} onChange={(e) => setRaw(e.target.value)} trailing={<span className="pr-2 text-sm text-fg-subtle">€</span>} />
            </Field>
            <div className="flex flex-wrap gap-2" role="group" aria-label="Montants rapides">
              {PRESETS.map((p) => (
                <Button key={p.label} size="sm" variant="secondary" onClick={() => setRaw(String(Math.round(status.current_value * p.share)))}>{p.label}</Button>
              ))}
            </div>
          </div>

          {result && (
            <div className="space-y-4" data-testid="pea-simulation">
              {result.closes ? (
                <Banner tone="warn" title="Ce retrait clôture votre PEA">
                  Avant 5 ans, un retrait entraîne la clôture du plan et l’imposition de tout son gain net, quel que soit le montant demandé (sauf licenciement, invalidité, création d’entreprise…).
                </Banner>
              ) : (
                <Banner tone="success" title="Le plan reste ouvert">
                  Après 5 ans, le retrait partiel ne supporte que les prélèvements sociaux sur sa part de plus-value.
                </Banner>
              )}
              <dl className="divide-y divide-line">
                <Row label={result.closes ? 'Montant récupéré (plan entier)' : 'Montant retiré'}><Money value={result.withdrawn} /></Row>
                <Row label={result.closes ? 'Gain net imposable (tout le plan)' : 'Part de plus-value'} hint={result.closes ? undefined : 'Au prorata gains / valeur du plan'}><Money value={result.taxableGain} /></Row>
                {result.closes && <Row label={`Impôt sur le revenu (${pctLabel(status.income_tax_rate_pct)})`}><Money value={-result.incomeTax} colorize /></Row>}
                <Row label={`Prélèvements sociaux (${pctLabel(status.social_rate_pct)})`}><Money value={-result.social} colorize /></Row>
                <Row label="Montant net perçu" strong><Money value={result.net} /></Row>
                {!result.closes && <Row label="Valeur restante du plan"><Money value={result.remainingValue} /></Row>}
                {!result.closes && (
                  <Row label="Plafond de versements" hint="Les retraits ne libèrent pas de plafond.">
                    <span className="text-fg-muted">inchangé · reste </span><Money value={status.remaining} decimals={0} />
                  </Row>
                )}
              </dl>
              {result.afterMaturity && (
                <Banner tone="gold" title={`En attendant le ${formatDate(status.maturity_date, 'long')}`}>
                  Le même retrait de <Money value={result.requested} decimals={0} /> laisserait le plan ouvert et coûterait <Money value={result.afterMaturity.taxes} decimals={0} /> de prélèvements sociaux, au lieu de <Money value={result.taxes} decimals={0} /> d’impôts et de prélèvements avec la clôture.
                </Banner>
              )}
            </div>
          )}
          <p className="text-xs leading-relaxed text-fg-subtle">
            Estimation indicative selon les règles en vigueur en 2026, hors taux historiques des prélèvements sociaux et cas particuliers. Ce n’est pas un conseil fiscal.
          </p>
        </div>
      )}
    </Card>
  );
};

const ProfileForm = ({ status, onSaved }) => {
  const toast = useToast();
  const [openedOn, setOpenedOn] = useState(status.opened_on || '');
  const [deposits, setDeposits] = useState(status.deposits ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    const parsed = String(deposits).trim() === '' ? null : Number(String(deposits).replace(/\s/g, '').replace(',', '.'));
    if (parsed !== null && !Number.isFinite(parsed)) {
      setError('Montant des versements invalide.');
      setSaving(false);
      return;
    }
    try {
      const res = await api.put('/pea/profile', { opened_on: openedOn || null, deposits: parsed });
      toast.success('Profil PEA enregistré');
      onSaved(res.data);
    } catch (err) {
      setError(errorText(err, 'Impossible d’enregistrer le profil PEA.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card as="form" onSubmit={save} className="lg:col-span-2" aria-label="Profil du PEA">
      <CardHeader title="Profil du PEA" description="Votre courtier ne transmet ni la date d’ouverture ni le total réellement versé." />
      <div className="space-y-4">
        <Field label="Date d’ouverture" htmlFor="pea-opened-on">
          <Input id="pea-opened-on" type="date" value={openedOn} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setOpenedOn(e.target.value)} />
        </Field>
        <Field label="Versements cumulés" htmlFor="pea-deposits" hint="Total versé depuis l’ouverture, hors plus-values et dividendes réinvestis.">
          <Input id="pea-deposits" inputMode="decimal" placeholder="ex. 98 000" value={deposits} onChange={(e) => setDeposits(e.target.value)} trailing={<span className="pr-2 text-sm text-fg-subtle">€</span>} />
        </Field>
        {error && <Banner tone="loss" role="alert">{error}</Banner>}
        <div className="flex justify-end">
          <Button type="submit" leadingIcon={Save} loading={saving}>Enregistrer</Button>
        </div>
      </div>
    </Card>
  );
};

const PEA = () => {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await api.get('/pea/status');
      setStatus(res.data);
    } catch (err) {
      setError(errorText(err, 'Impossible de charger le compteur PEA.'));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-8">
      <PageHeader eyebrow="Patrimoine" title="Mon PEA" description="Plafond des versements, antériorité fiscale et simulation de retrait." />
      {error && <Banner tone="loss" role="alert" action={<Button size="sm" variant="secondary" onClick={load}>Réessayer</Button>}>{error}</Banner>}
      {!status && !error && (
        <div className="grid gap-6 lg:grid-cols-3" role="status">
          <span className="sr-only">Chargement du compteur PEA…</span>
          {[0, 1, 2].map((i) => <Card key={i} aria-hidden="true"><Skeleton className="h-4 w-32" /><Skeleton className="mt-6 h-8 w-40" /><Skeleton className="mt-5 h-2 w-full" /></Card>)}
        </div>
      )}
      {status && (
        <>
          {status.profile_incomplete && (
            <Banner tone="gold" title="Complétez le profil de votre PEA">
              La date d’ouverture et le total de vos versements activent le compteur du plafond, la date des 5 ans et le simulateur de retrait.
            </Banner>
          )}
          <div className="grid gap-6 lg:grid-cols-3">
            <CeilingCard status={status} />
            <MaturityCard status={status} />
            <GainsCard status={status} />
          </div>
          <div className="grid gap-6 lg:grid-cols-5 lg:items-start">
            <Simulator status={status} />
            <ProfileForm key={`${status.opened_on}-${status.deposits}`} status={status} onSaved={setStatus} />
          </div>
        </>
      )}
    </div>
  );
};

export default PEA;
